import {
    isExpectedDecodeMiss,
    ScannerError,
    normalizeError,
} from '../utils/errors.js';

export class BarcodeDetectorAdapter {
    constructor({
        logger,
        windowLike = globalThis.window,
    } = {}) {
        this.name = 'BarcodeDetector';
        this.logger = logger;
        this.window = windowLike;
        this.detector = null;
        this.video = null;
        this.running = false;
        this.frameHandle = null;
        this.consecutiveErrors = 0;
        this.onDetected = null;
        this.onFrame = null;
        this.onError = null;
        this.minimumFrameIntervalMs = 0;
        this.lastProcessedAt = null;
    }

    static isSupported(windowLike = globalThis.window) {
        return typeof windowLike?.BarcodeDetector === 'function';
    }

    async start({
        video,
        onDetected,
        onFrame,
        onError,
        processingFrameRate,
    }) {
        if (!BarcodeDetectorAdapter.isSupported(this.window)) {
            throw new ScannerError(
                'BARCODE_DETECTOR_UNAVAILABLE',
                'BarcodeDetector não suportado neste navegador.',
                { context: 'adapter.barcode-detector' }
            );
        }

        const Detector = this.window.BarcodeDetector;
        let supportedFormats = [];

        try {
            if (typeof Detector.getSupportedFormats === 'function') {
                supportedFormats = await Detector.getSupportedFormats();
            }

            this.detector = supportedFormats.length > 0
                ? new Detector({ formats: supportedFormats })
                : new Detector();
        } catch (error) {
            throw new ScannerError(
                'BARCODE_DETECTOR_INITIALIZATION_FAILED',
                'BarcodeDetector existe, mas falhou ao inicializar.',
                {
                    cause: error,
                    recoverable: true,
                    context: 'adapter.barcode-detector',
                }
            );
        }

        this.video = video;
        this.running = true;
        this.consecutiveErrors = 0;
        this.onDetected = onDetected;
        this.onFrame = onFrame;
        this.onError = onError;
        this.minimumFrameIntervalMs = processingFrameRate > 0
            ? 1000 / processingFrameRate
            : 0;
        this.lastProcessedAt = null;

        this.logger?.info(
            'adapter.barcode_detector.started',
            'BarcodeDetector inicializado.',
            { supportedFormats }
        );

        this.scheduleNextFrame();

        return {
            name: this.name,
            supportedFormats,
        };
    }

    scheduleNextFrame() {
        if (!this.running) {
            return;
        }

        if (typeof this.video.requestVideoFrameCallback === 'function') {
            this.frameHandle = this.video.requestVideoFrameCallback(
                (timestamp) => this.scanFrame(timestamp)
            );
            return;
        }

        this.frameHandle = this.window.requestAnimationFrame(
            (timestamp) => this.scanFrame(timestamp)
        );
    }

    async scanFrame(timestamp = null) {
        if (!this.running) {
            return;
        }

        const startedAt = Number.isFinite(timestamp)
            ? timestamp
            : globalThis.performance?.now?.() ?? Date.now();

        if (
            this.lastProcessedAt !== null
            && startedAt - this.lastProcessedAt < this.minimumFrameIntervalMs
        ) {
            this.scheduleNextFrame();
            return;
        }

        this.lastProcessedAt = startedAt;
        let detectedCount = 0;

        try {
            if (this.video.readyState >= 2) {
                const barcodes = await this.detector.detect(this.video);

                this.consecutiveErrors = 0;
                detectedCount = barcodes.length;

                barcodes.forEach((barcode) => {
                    this.onDetected?.({
                        text: String(barcode.rawValue || '').trim(),
                        format: barcode.format || 'Não informado',
                        raw: barcode,
                    });
                });
            }
        } catch (error) {
            if (isExpectedDecodeMiss(error)) {
                this.consecutiveErrors = 0;
                return;
            }

            this.consecutiveErrors++;

            this.onError?.(
                normalizeError(error, 'adapter.barcode-detector.scan'),
                { fatal: this.consecutiveErrors >= 3 }
            );
        } finally {
            const finishedAt = globalThis.performance?.now?.() ?? Date.now();

            this.onFrame?.({
                detected: detectedCount > 0,
                detectedCount,
                durationMs: Math.max(0, finishedAt - startedAt),
            });
            this.scheduleNextFrame();
        }
    }

    pause() {
        this.running = false;

        if (this.frameHandle !== null) {
            if (
                this.video
                && typeof this.video.cancelVideoFrameCallback === 'function'
            ) {
                this.video.cancelVideoFrameCallback(this.frameHandle);
            } else {
                this.window?.cancelAnimationFrame?.(this.frameHandle);
            }
        }

        this.frameHandle = null;
    }

    resume() {
        if (!this.detector || !this.video || this.running) {
            return;
        }

        this.running = true;
        this.scheduleNextFrame();
    }

    stop() {
        this.pause();
        this.detector = null;
        this.video = null;
        this.onDetected = null;
        this.onFrame = null;
        this.onError = null;
        this.lastProcessedAt = null;
    }
}
